import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../environments/environment';
import {flatMap, map, mergeMap} from 'rxjs/operators';
import {
  DetailedRegistration,
  IDetailedRegistration,
  IRegistration,
  ParticipantsCounter,
  Registration
} from './datamodel/registration';
import {DetailedCourse, ICourse, IDetaildedCourse} from './datamodel/course';
import {Observable, of} from 'rxjs';
import {isUndefined} from 'underscore';

import { HttpParameterCodec } from '@angular/common/http';

export class CustomHttpParamEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

interface ServerInfo {
  era: number;
}

@Injectable({
  providedIn: 'root'
})
export class BackendService {
  private cachedEra: number;

  constructor(public http: HttpClient) {
  }

  public get<T>(path, params) {
    let httpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value.toString());
    }

    return this.http.get<T>(
      environment.backendUrl + path, {params: httpParams});
  }

  public serverInfo() {
    return this.get<ServerInfo>('server_info', {});
  }

  public eraObservable(): Observable<number> {
    if (isUndefined(this.cachedEra)) {
      return this.serverInfo().pipe(
        map(response => this.cachedEra = response.era)
      );
    }  else {
      return of(this.cachedEra);
    }
  }

  public getRegistrations(): Observable<Registration[]> {
    return this.eraObservable().pipe(
      flatMap((era: number) => this.get<IRegistration[]>('registrations', {era})),
      map((rs: IRegistration[]) => rs.map(r => new Registration(r)).sort((r1, r2) => r1.id < r2.id ? -1 : 1))
    );
  }

  public getDetailedRegistration(id: string) {
    return this.eraObservable().pipe(
      flatMap((era: number) => this.get<IDetailedRegistration>('registrations', {era, registration_id: id})),
      map(registration => new DetailedRegistration(registration))
    );
  }

  getCourse(courseId: string, termId: string, registrationId: string) {
    return this.eraObservable().pipe(
      flatMap(era => this.get<IDetaildedCourse>('courses', {era, course_id: courseId, term_id: termId, registration_id: registrationId})),
      map(course => new DetailedCourse(course))
    );
  }

  public getRegisteredCount(id: string): Observable<ParticipantsCounter> {
    return this.get<object>(
      'registrations/participants',
      {registration_id: id, epoch: 0}
    ).pipe(map(
      data => new ParticipantsCounter(data)
    ));
  }
}
